import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Users, Calendar, FileText, Wallet, MessageSquare, Building2, KeyRound, ClipboardCheck, FileSpreadsheet, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API, authQuery, downloadUrl } from "../lib/api";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="gold-card"
      style={{
        padding: "1.1rem 1.2rem",
        display: "flex",
        gap: 14,
        alignItems: "center",
        background: "#fff",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 8,
          background: accent || "#fdf4e0",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon size={22} color="#9a7530" />
      </div>
      <div>
        <div className="kv-label" style={{ fontSize: "0.62rem" }}>
          {label}
        </div>
        <div style={{ fontFamily: "Cinzel, serif", fontSize: "1.5rem", color: "#0d1b2a" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, count, testid }) {
  return (
    <div className="mt-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} color="#9a7530" />
        <h2
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "1.1rem",
            letterSpacing: "0.14em",
            color: "#0d1b2a",
            textTransform: "uppercase",
            margin: 0,
          }}
        >
          {title}
        </h2>
        {typeof count === "number" && (
          <span className="badge-grade" style={{ marginLeft: 6 }}>
            {count}
          </span>
        )}
      </div>
      <div className="gold-divider mb-3" />
      {children}
    </div>
  );
}

export default function JurisdictionLodgeOverviewPage() {
  const { t, i18n } = useTranslation();
  const { lodgeId } = useParams();
  const navigate = useNavigate();
  const locale =t("jurisdictionLodgeOverview.en_gb");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/jurisdiction-lodges/${lodgeId}/overview`);
        if (!cancel) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancel) setBusy(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [lodgeId]);

  if (busy) {
    return (
      <div className="auth-bg flex items-center justify-center" style={{ minHeight: "60vh" }}>
        <div className="spinner" />
      </div>
    );
  }
  if (!data) return null;

  const lodge = data.lodge || {};
  const totals = data.totals || {};
  const dueAmt = (n) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "RON", maximumFractionDigits: 0 }).format(n || 0);
  const dt = (s) => (s ? new Date(s).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <div data-testid="jurisdiction-overview-page">
      <button
        type="button"
        onClick={() => navigate("/users")}
        className="btn-outline-gold flex items-center gap-1 mb-4"
        data-testid="overview-back"
      >
        <ArrowLeft size={12} /> {t("common.back", "Înapoi")}
      </button>

      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div className="flex items-start gap-3">
          <Building2 size={28} color="#c9a84c" />
          <div>
            <div className="kv-label" style={{ fontSize: "0.62rem", letterSpacing: "0.18em" }}>
              {t("jurisdiction.title")}
            </div>
            <h1
              style={{
                fontFamily: "Cinzel, serif",
                fontSize: "2rem",
                color: "#0d1b2a",
                letterSpacing: "0.06em",
                margin: 0,
              }}
            >
              {lodge.name}
            </h1>
            <div className="kv-label" style={{ marginTop: 4 }}>
              {lodge.lodge_number ? `Nr. ${lodge.lodge_number} · ` : ""}
              {lodge.address || "—"} · {lodge.obedience_abbreviation || "—"}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => downloadUrl(`${API}/jurisdiction-lodges/${lodgeId}/overview/export.pdf${authQuery()}`)}
            className="btn-primary-gold flex items-center gap-2"
            data-testid="overview-export-pdf"
            style={{ textDecoration: "none", cursor: "pointer" }}
          >
            <FileDown size={14} /> {t("overview.exportPdf", "EXPORT PDF")}
          </button>
          <button
            type="button"
            onClick={() => downloadUrl(`${API}/jurisdiction-lodges/${lodgeId}/overview/export.csv${authQuery()}`)}
            className="btn-outline-gold flex items-center gap-2"
            data-testid="overview-export-csv"
            style={{ textDecoration: "none", background: "transparent", border: "1px solid #c9a84c", cursor: "pointer" }}
          >
            <FileSpreadsheet size={14} /> {t("overview.exportExcel", "EXPORT EXCEL")}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginTop: "1.5rem",
        }}
      >
        <StatCard icon={Users} label={t("members.title", "Membri")} value={`${totals.active_members || 0}/${totals.members || 0}`} />
        <StatCard icon={Calendar} label={t("events.title", "Evenimente")} value={totals.events || 0} />
        <StatCard icon={FileText} label={t("documents.title", "Documente")} value={totals.documents || 0} />
        <StatCard icon={ClipboardCheck} label={t("attendance.title", "Prezențe")} value={totals.attendance || 0} />
        <StatCard icon={Wallet} label={t("dues.title", "Cotizații")} value={dueAmt(totals.dues_total)} />
        <StatCard icon={KeyRound} label={t("users.title", "Utilizatori")} value={totals.users || 0} />
      </div>

      <Section title={t("members.title", "Membri")} icon={Users} count={totals.members} testid="overview-members">
        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table">
            <thead>
              <tr>
                <th>{t("members.firstName", "Prenume")}</th>
                <th>{t("members.lastName", "Nume")}</th>
                <th>{t("members.grade", "Grad")}</th>
                <th>{t("members.status", "Status")}</th>
                <th>{t("members.passportNumber", "Pașaport")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.members || []).length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: "1.5rem", fontStyle: "italic", color: "#858481" }}>
                  {t("common.empty", "Nicio înregistrare.")}
                </td></tr>
              ) : (
                data.members.map((m) => (
                  <tr key={m.id} data-testid={`overview-member-${m.id}`}>
                    <td>{m.first_name}</td>
                    <td>{m.last_name}</td>
                    <td>{m.grade || "—"}</td>
                    <td>{m.status || "—"}</td>
                    <td>{m.passport_number || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("events.title", "Evenimente")} icon={Calendar} count={totals.events} testid="overview-events">
        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table">
            <thead>
              <tr>
                <th>{t("common.date", "Data")}</th>
                <th>{t("events.title", "Titlu")}</th>
                <th>{t("events.location", "Locație")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.events || []).length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "1.5rem", fontStyle: "italic", color: "#858481" }}>
                  —
                </td></tr>
              ) : (
                data.events.map((e) => (
                  <tr key={e.id}>
                    <td>{dt(e.date)}</td>
                    <td>{e.title}</td>
                    <td>{e.location || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("documents.title", "Documente")} icon={FileText} count={totals.documents} testid="overview-documents">
        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table">
            <thead>
              <tr>
                <th>{t("documents.title", "Titlu")}</th>
                <th>{t("documents.category", "Categorie")}</th>
                <th>{t("common.date", "Data")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.documents || []).length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "1.5rem", fontStyle: "italic", color: "#858481" }}>—</td></tr>
              ) : (
                data.documents.map((d) => (
                  <tr key={d.id}>
                    <td>{d.title}</td>
                    <td>{d.category || "—"}</td>
                    <td>{dt(d.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("dues.title", "Cotizații")} icon={Wallet} count={(data.dues || []).length} testid="overview-dues">
        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table">
            <thead>
              <tr>
                <th>{t("members.title", "Membru")}</th>
                <th>{t("dues.period", "Perioadă")}</th>
                <th>{t("dues.amount", "Sumă")}</th>
                <th>{t("dues.status", "Status")}</th>
                <th>{t("dues.dueDate", "Scadență")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.dues || []).length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: "1.5rem", fontStyle: "italic", color: "#858481" }}>—</td></tr>
              ) : (
                data.dues.map((d) => (
                  <tr key={d.id}>
                    <td>{d.member_name}</td>
                    <td>{d.period_label}</td>
                    <td>{d.amount} {d.currency}</td>
                    <td>{d.status}</td>
                    <td>{dt(d.due_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("users.title", "Utilizatori")} icon={KeyRound} count={totals.users} testid="overview-users">
        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table">
            <thead>
              <tr>
                <th>{t("jurisdiction.users.name", "Nume")}</th>
                <th>{t("login.username", "Username")}</th>
                <th>{t("users.role", "Rol")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.users || []).length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "1.5rem", fontStyle: "italic", color: "#858481" }}>—</td></tr>
              ) : (
                data.users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.username}</td>
                    <td>{u.role}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title={t("chat.title", "Chat")}
        icon={MessageSquare}
        count={(data.recent_chat || []).length}
        testid="overview-chat"
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid var(--cream-200)",
            padding: "1rem",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {(data.recent_chat || []).length === 0 ? (
            <div style={{ textAlign: "center", color: "#858481", fontStyle: "italic" }}>—</div>
          ) : (
            data.recent_chat.map((m) => (
              <div key={m.id} style={{ marginBottom: "0.6rem", paddingBottom: "0.5rem", borderBottom: "1px solid #f6efde" }}>
                <div className="kv-label" style={{ fontSize: "0.6rem" }}>
                  {m.sender_name || "—"} · {dt(m.created_at)}
                </div>
                <div style={{ marginTop: 2 }}>{m.body}</div>
              </div>
            ))
          )}
        </div>
      </Section>
    </div>
  );
}
